/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getConfig} from 'coreUtils/utils';
import {removeSiteAliasPrefix} from 'coreUtils/siteAliases';

import {getSiteByReference} from 'coreUtils/siteUtils';

import {isServer} from './utils';
import {PageType} from './IUtils';

export type PARSED_URL = {
	pathname: string;
	search?: string;
};

const PROTOCOL_AND_HOST_REG = RegExp('(http[s]:(//[^/?#]+)?)?', 'g');

/**
 * Updates the URL with the given country and language code
 * @param {string} url - The URL to update.
 * @param {string} languageCode - Locale code to update the URL with.
 * @returns {string} - The updated URL
 */
export const normalizeUrl = (url: string, languageCode: string): string => {
	const regex = new RegExp(`/[a-z][a-z]-[a-z][a-z]/`, 'i');

	return url.replace(regex, `/${languageCode}/`);
};

/**
 * A function that takes a path and qualifies it with the current host and protocol.
 * This function works on the client and on the server.
 *
 * @example
 * absoluteUrl(/women/dresses?color=black)
 *
 * // => http(s)://www.site.com/women/dresses?color=black
 * @param {String} path - path
 * @returns {string|*}
 */
export const absoluteUrl = (path: string): string => {
	const parsedURL = parseURL(path);

	return `${getAppOrigin()}${parsedURL.pathname}${parsedURL.search}`;
};

/**
 * Modifies a given url by adding/updating query parameters.
 *
 * @param {string} url - The base url of the output url set.
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string} A URL with additional params
 * @example
 * import {rebuildPathWithParams} from '/path/to/utils/url'
 *
 * rebuildPathWithParams(
 *     '/en-GB/product/25501032M',
 *     {color: 'JJ2SKXX', size: 'MD'}
 * )
 *
 * // Returns
 * // '/en-GB/product/25501032M?color=JJ2SKXX&size=9MD'
 */
export const rebuildPathWithParams = (url: string, extraParams: object): string => {
	if (!url) {
		return '';
	}

	const [pathname, search] = url?.split('?');
	const params = new URLSearchParams(search);

	// Apply any extra params.
	Object.keys(extraParams).forEach((key) => {
		const value = extraParams[key];

		// 0 is a valid value as for a param
		if (!value && value !== 0) {
			params.delete(key);
		} else {
			params.set(key, value);
		}
	});

	// Clean up any trailing `=` for params without values.
	const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '');

	// Generate the newly updated url.
	return `${pathname}${Array.from(paramStr).length > 0 ? `?${paramStr}` : ''}`;
};

/**
 * Builds a list of modified Urls with the provided params key and values,
 * preserving any search params provided in the original url.Optionally
 * you can pass and object used to set static params values.
 * @param {string} url - The base url of the output url set.
 * @param {string} key - The search params for the associated values
 * @param {Array} values - The search param values
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string[]} A list of URLs
 * @example
 * import {buildUrlSet} from '/path/to/utils/url'
 *
 * buildUrlSet(
 *     '/womens/clothing',
 *     'sort',
 *     ['price-high-to-low', 'price-low-to-high'],
 *     {offset: 0}
 * )
 *
 * // Returns
 * // ['/womens/clothing?sort=price-high-to-low', '/womens/clothing?sort=price-low-to-high']
 */
export const buildUrlSet = (url: string = '', key: string = '', values = [], extraParams: object = {}): string[] =>
	values.map((value) => rebuildPathWithParams(url, {[key]: value, ...extraParams}));

/**
 * Retruns true if the provided URL points to the SFCC instance
 * @param {String} stringUrl - the target URL
 * @return {Boolean}
 */
export const isSfccURL = (stringUrl: string): boolean => {
	try {
		const url = new URL(stringUrl);
		const config = getConfig();

		return (
			['stg-pwa-fa.thelevelgroup.com', config.app.instanceHostname, config.app.noHybridHostname, config.app.sfccHostname].indexOf(
				url.hostname,
			) !== -1
		);
	} catch (e) {
		return false;
	}
};

type TUrlCheckerOptions = {
	// If set to true, the protocol (http or https) is ignored. This means that 'http://example.com'
	// and 'https://example.com' will be treated as having the same origin.
	ignoreProtocol?: boolean;

	// If set to true, the pathname of the URL is ignored. This means that 'example.com/page1'
	// and 'example.com/page2' will be treated as the same.
	ignorePathname?: boolean;

	// If set to true, subdomains are ignored. This implies 'sub.example.com' and 'example.com'
	// are treated as having the same origin.
	ignoreSubdomains?: boolean;

	// In cases where the URL format is unrecognizable or doesn't match any particular pattern,
	// this option determines the default behavior. If set to true, such URLs will be treated as internal.
	defaultInternal?: boolean;

	// A list of domains that should always be treated as internal. This takes precedence over other checks.
	// For instance, if 'external.com' is in the allowList, it will be treated as internal even if it's
	// not part of the base URL domain.
	allowList?: string[];

	// A list of domains that should always be treated as external, regardless of other conditions.
	// This takes precedence over the allowList. So, if a domain is present in both lists, it will be treated as external.
	denyList?: string[];
};

/**
 * Check if a given url is internal
 * This function is a starting point for a more robust solution, URLManager that can
 * take a lot of the responsibility of managing URLs away from the app.
 * @param {string} url - url
 * @param {TUrlCheckerOptions} options - options
 * @return {boolean}
 */
export const getIsInternalUrl = (url: string, options?: TUrlCheckerOptions): boolean => {
	// Quick checks based on the old function's behavior
	if (!url || url === '#') {
		return true;
	}

	// /on/ is related to SFCC links
	if (url.indexOf('/on/') === 0) {
		return false;
	}

	const baseUrl = getAppOrigin();

	// If it's a potential relative URL, then it's internal.
	if (isRelative(url)) {
		return true;
	}

	const defaultPortForScheme = (scheme: string) => {
		switch (scheme.toLowerCase()) {
			case 'http:':
				return '80';
			case 'https:':
				return '443';
			case 'ftp:':
				return '21';
			case 'ftps:':
				return '990';
			default:
				return null;
		}
	};

	const extractComponents = (inputUrl: string) => {
		// Modified regex to handle different schemes and be more robust
		const match = inputUrl.match(/^([a-zA-Z][a-zA-Z\d+\-.]*):\/\/([^:/\s]+)(:([0-9]+))?([^#\s]*)/i);

		if (!match) {
			return null;
		}

		return {
			protocol: options?.ignoreProtocol ? null : match[1],
			hostname: match[2],
			port: match[4] || defaultPortForScheme(match[1]),
			pathname: match[5] || '/',
		};
	};

	const parsedUrl = extractComponents(url);
	const parsedBase = extractComponents(baseUrl);

	if (!parsedUrl || !parsedBase) {
		return options?.defaultInternal ?? false;
	}

	// Check for allowList and denyList
	if (options?.allowList?.includes(parsedUrl.hostname)) {
		return true;
	}

	if (options?.denyList?.includes(parsedUrl.hostname)) {
		return false;
	}

	// If no options are provided, default to old function behavior
	if (!options) {
		return parsedUrl.hostname === parsedBase.hostname;
	}

	const baseParts = parsedBase.hostname.split('.');
	const urlParts = parsedUrl.hostname.split('.');
	const isSameSubdomain = options.ignoreSubdomains && baseParts.slice(-2).join('.') === urlParts.slice(-2).join('.');

	const hasSameOrigin =
		(parsedUrl.protocol === parsedBase.protocol || options.ignoreProtocol) &&
		(parsedUrl.hostname === parsedBase.hostname || isSameSubdomain) &&
		parsedUrl.port === parsedBase.port;

	const hasSamePath = options.ignorePathname || parsedUrl.pathname.startsWith(parsedBase.pathname);

	return hasSameOrigin && hasSamePath;
};

/**
 * Original getAppOrigin override to support hybrid deployment
 */
export const isHybridActive = (): boolean => {
	const config = getConfig();
	// TODO: Solution for DHP-857. Hybrid deployment requests must be on the hybrid host name
	const HYBRID_ORIGIN_URL = `${config.app.instanceProtocol}://${config.app?.hybridLocalHostname}`;

	return !isServer && HYBRID_ORIGIN_URL === `${window.location.protocol}//${window.location.host}`;
};

/**
 * Original getMrtOrigin override to support hybrid deployment
 */
export const getMrtOrigin = (): string => {
	const config = getConfig();

	return `${config.app.instanceProtocol}://${config.app?.mrtHostname}`;
};

/**
 * Original getAppOrigin override to support hybrid deployment
 */
export const getAppOrigin = (): string => {
	const config = getConfig();

	return `${config.app.instanceProtocol}://${config.app.instanceHostname}`;
};

/**
 * Changes the current url
 * @param {String} url - the destination url
 */
export const setTabURL = (url: string): void => {
	if (window && url !== window.location.pathname) {
		// Keeps the router state of the entry (its key identifies the page on back/forward, see `pageSlots`).
		window.history.replaceState(window.history.state, '', url);
	}
};

/**
 * Return the current url decoded if necessary
 * @param {String} url - url
 * @return {String}
 */
export const getDecodedURI = (url: string): string => {
	return url === decodeURI(url) ? url : decodeURI(url);
};

/**
 * Return the encoded uri parts if necessary
 * @param {String} url - url
 * @return {String}
 */
export const getEncodedURI = (url: string): string => {
	const urlParts = url.split('/');

	for (let i = 0; i < urlParts.length; i++) {
		const value = urlParts[i];

		if (value === decodeUriComponentWithTryCatch(value)) {
			urlParts[i] = encodeURIComponent(value);
		}
	}

	return urlParts.join('/');
};

/**
 * Appends query parameters to a URL.
 * @param {string} url - The original URL.
 * @param {Object} params - An object containing the query parameters.
 * @returns {string} The modified URL with the appended query parameters.
 */
export const appendParamsToURL = (url: string, params: Record<string, string>) => {
	const {pathname, search} = parseURL(url);
	const searchParams = new URLSearchParams(search);

	Object.keys(params || {}).forEach((key) => {
		searchParams.set(key, params[key]);
	});

	const updatedURL = searchParams.size > 0 ? `${pathname}?${searchParams.toString()}` : pathname;

	// If the original URL was relative, return a relative URL
	return isRelative(url) ? updatedURL : `${getAppOrigin()}${updatedURL}`;
};

/**
 * Removes query parameters from URL.
 * @param {string} url - The original URL.
 * @param {Array<string>} params - An array containing the query parameters to remove.
 * @returns {string} The modified URL without query parameters.
 */
export const removeParamsFromURL = (url: string, params: string[]) => {
	const {pathname, search} = parseURL(url);
	const searchParams = new URLSearchParams(search);

	params.forEach((key) => {
		searchParams.delete(key);
	});

	const updatedURL = searchParams.size ? `${pathname}?${searchParams.toString()}` : pathname;

	// If the original URL was relative, return a relative URL
	return isRelative(url) ? updatedURL : `${getAppOrigin()}${updatedURL}`;
};

/**
 * Checks if a URL is relative.
 * @param {string} url - The URL to check.
 * @returns {boolean} True if the URL is relative, false otherwise.
 */
const isRelative = (url: string) => {
	return !/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url);
};

/**
 * Parses pat and returns pathname and search separated
 * if possible prefer this to URL object as it is faster
 * @param {string} path - original pathname
 * @returns {PARSED_URL}
 */
export const parseURL = (path: string): PARSED_URL => {
	let pathname, search;

	if (typeof path === 'string') {
		[pathname, search] = path.replace(PROTOCOL_AND_HOST_REG, '').split('?');

		if (search === undefined) {
			search = '';
		} else {
			search = `?${search}`;
		}

		if (!pathname.startsWith('/')) {
			pathname = `/${pathname}`;
		}
	}

	return {pathname, search};
};

/**
 * Return the current url without prefix alias, e.g '/{country}/en'
 * @param {String} path - original pathname
 * @returns {String}
 */
export const removeCountryPrefix = (path) => removeSiteAliasPrefix(path, getConfig().app?.siteAliases);

/**
 * Returns the pathname without the locale prefix.
 * @returns {string}
 */
export const getPathnameWithoutLocale = () => {
	const fullPath = window.location.pathname;
	const locale = /^\/.{2}-.{2}/;
	const pathnameWithoutLocale = fullPath.replace(locale, '');

	return pathnameWithoutLocale;
};

/**
 * Creates search parameters string from key value pairs
 * @param {Object} searchParameters - object with key value pairs
 * @returns {String} search parameters string
 */
export const createUrlSearchParameters = (searchParameters: Record<string, string>) => {
	const searchParams = new URLSearchParams();

	Object.keys(searchParameters || {}).forEach((key) => {
		searchParams.set(key, searchParameters[key]);
	});

	return searchParams.toString();
};

/**
 * Convenient method to append path to the url without the need to manage trailing slashes
 * @param {string} baseUrl - base url like https://example.com
 * @param {string} path - path
 * @returns {string} url with path
 */
export const appendPathToUrl = (baseUrl: string, path: string): string => {
	const url = new URL(baseUrl);
	url.pathname = path;

	return url.toString();
};

export const decodeUriComponentWithTryCatch = (url) => {
	try {
		return decodeURIComponent(url);
	} catch (e) {
		console.warn('decodeURIComponent failed ', e);

		return url;
	}
};

/**
 * Get page type ("HP", "PLP", "PDP" or "404") from the location.currentRoute string
 * @param {string} currentRoute - current route
 * @returns {PageType|undefined} page type
 */
export const getPageTypeFromUrl = (currentRoute: string): PageType | void => {
	if (!currentRoute) {
		return;
	}

	switch (true) {
		case currentRoute.includes('Content-Show'):
			return PageType.Editorial;
		case currentRoute.includes('Search-Show'):
			return PageType.PLP;
		case currentRoute.includes('Product-Show'):
			return PageType.PDP;
		case currentRoute.includes('SplashPage'):
			return PageType.SplashPage;
		case currentRoute.includes('MyAccount'):
			return PageType.MY_ACCOUNT;
		case currentRoute.includes('NotFound'):
			return PageType.NOT_FOUND;
		default:
			return;
	}
};

/**
 * Returns the url related to the country passed as countryCode defined in sites.js
 * @param {string} countryCode
 * @param {string} siteId
 * @param {string} instanceKey
 * @returns {string} Hostname related to Country
 */
export const getHostnameByCountryCode = (
	countryCode: string,
	siteId: string,
	instanceKey: string
): string | null => {
	let hostname = null;
	const countryRelatedHostname = getSiteByReference(siteId)?.countryRelatedHostnames?.[instanceKey];

	if (countryRelatedHostname) {
		hostname =
			countryCode in countryRelatedHostname
				? countryRelatedHostname[countryCode]
				: countryRelatedHostname['DEFAULT'];
	}

	return hostname;
};

/**
 * Returns the url without specified section
 * @param {string} url - The original URL
 * @param {string} partToRemove - The part which should be deleted from the url
 * @returns {string} modified url
 */
export const removeUrlPart = (url: string, partToRemove: string) => {
	const urlParts = url.split('/');
	const filteredUrlParts = urlParts.filter(part => part !== partToRemove);
	const updatedUrl = filteredUrlParts.join('/');

	return updatedUrl;
};

/**
 * Determines the page referrer based on the provided URL and first visit status.
 *
 * @param {string} url - The current page URL.
 * @param {boolean} isFirstVisit - Whether this is the first visit to the site.
 * @returns {string} The determined page referrer URL.
 */
export const getPageReferrer = (isFirstVisit: boolean): string => {
	const url = window.location.href;
	const navigationEntry = window.performance.getEntriesByType('navigation')[0] as
		| PerformanceNavigationTiming
		| undefined;
	const navigationType = navigationEntry?.type;
	const storedReferrer = sessionStorage.getItem('pageReferrer') || '';
	const storedUrl = sessionStorage.getItem('currentPageUrl') || '';

	let referrer = '';

	if (isFirstVisit) {
		referrer = document.referrer || '';
		sessionStorage.setItem('currentPageUrl', url);
		sessionStorage.setItem('pageReferrer', referrer);

		return referrer;
	}

	if (navigationType === 'reload') {
		referrer = storedReferrer;
	} else if (navigationType === 'navigate') {
		referrer = storedUrl;

		if (storedUrl === url) {
			referrer = storedReferrer;
		}
	} else {
		referrer = document.referrer || storedUrl;
	}

	sessionStorage.setItem('currentPageUrl', url);

	if (referrer && referrer !== url && referrer !== storedReferrer) {
		sessionStorage.setItem('pageReferrer', referrer);
	}

	return referrer;
};

/**
 * Safe wrapper for decodeURIComponent
 * @param {String} value
 * @returns {String}
 */
export const safeDecodeURIComponent = (value: string) : string => {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
};

/**
 * Safe wrapper for decodeURI
 * @param {String} value
 * @returns {String}
 */
export const safeDecodeURI = (value: string) : string => {
	if (typeof value !== 'string') {
		return value;
	}

	try {
		return decodeURI(value);
	} catch {
		return value;
	}
};
